import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { X, UserPlus, Search, Repeat } from "lucide-react";

const inputCls = "w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal placeholder:text-slate focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30";

/**
 * Modal to create an appointment as staff.
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onCreated: (appt) => void
 *   defaultStart: Date | null
 *   defaultPractitionerId?: string
 *   defaultRoom?: string
 */
export default function NewAppointmentDialog({ open, onClose, onCreated, defaultStart, defaultPractitionerId }) {
  const [services, setServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);

  const [serviceId, setServiceId] = useState("");
  const [start, setStart] = useState("");
  const [practitionerId, setPractitionerId] = useState("");

  const [clientMode, setClientMode] = useState("existing"); // existing | new
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const [intake, setIntake] = useState({
    first_name: "", last_initial: "", email: "", phone: "",
    sms_opt_in: false, preferred_channel: "email",
  });

  // Recurring weeks (1..4)
  const [weeks, setWeeks] = useState(1);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load services + practitioners on open
  useEffect(() => {
    if (!open) return;
    api.get("/services").then(({ data }) => setServices(data.filter((s) => !s.walk_in_only)));
    api.get("/practitioners").then(({ data }) => setPractitioners(data));
  }, [open]);

  // Set defaults whenever dialog opens
  useEffect(() => {
    if (!open) return;
    setError("");
    setServiceId(""); setPractitionerId(defaultPractitionerId || "");
    setSelectedClient(null); setClientQuery(""); setClientResults([]);
    setIntake({ first_name: "", last_initial: "", email: "", phone: "", sms_opt_in: false, preferred_channel: "email" });
    setWeeks(1);
    if (defaultStart instanceof Date) {
      // Convert to local datetime-local format YYYY-MM-DDTHH:MM
      const pad = (n) => String(n).padStart(2, "0");
      const v = `${defaultStart.getFullYear()}-${pad(defaultStart.getMonth() + 1)}-${pad(defaultStart.getDate())}T${pad(defaultStart.getHours())}:${pad(defaultStart.getMinutes())}`;
      setStart(v);
    } else {
      setStart("");
    }
  }, [open, defaultStart, defaultPractitionerId]);

  // Client search (debounced)
  useEffect(() => {
    if (!open || clientMode !== "existing") return;
    const t = setTimeout(() => {
      if (!clientQuery.trim()) { setClientResults([]); return; }
      api.get("/clients", { params: { search: clientQuery } }).then(({ data }) => setClientResults(data.slice(0, 6)));
    }, 200);
    return () => clearTimeout(t);
  }, [clientQuery, clientMode, open]);

  if (!open) return null;

  const canSubmit = serviceId && start && (
    (clientMode === "existing" && selectedClient) ||
    (clientMode === "new" && intake.first_name && intake.last_initial && intake.email && intake.phone)
  );

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const localDate = new Date(start);
      const starts = Array.from({ length: weeks }, (_, i) => new Date(localDate.getTime() + i * 7 * 86400000).toISOString());
      if (weeks > 1) {
        const payload = {
          service_id: serviceId,
          starts,
          practitioner_id: practitionerId || null,
          status: "confirmed",
        };
        if (clientMode === "existing") payload.client_id = selectedClient.id;
        else payload.client_intake = intake;
        const { data } = await api.post("/appointments/recurring", payload);
        onCreated?.(data);
      } else {
        const payload = {
          service_id: serviceId,
          start: localDate.toISOString(),
          practitioner_id: practitionerId || null,
          status: "confirmed",
        };
        if (clientMode === "existing") payload.client_id = selectedClient.id;
        else payload.client_intake = intake;
        const { data } = await api.post("/appointments", payload);
        onCreated?.(data);
      }
      onClose?.();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 animate-fade-in" data-testid="new-appt-dialog">
      <div className="bg-white border border-hairline rounded-xl shadow-warm-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-sage">New appointment</p>
            <h2 className="font-display text-xl text-charcoal mt-1">Schedule a visit</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate hover:text-forest rounded-md" aria-label="Close" data-testid="new-appt-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {error && <div className="px-3 py-2 bg-status-danger-bg text-status-danger text-sm font-ui rounded-md" data-testid="new-appt-error">{error}</div>}

          {/* Service + time */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Service" required>
              <select className={inputCls} value={serviceId} onChange={(e) => setServiceId(e.target.value)} data-testid="new-appt-service">
                <option value="">Pick a service…</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.duration_minutes}m</option>)}
              </select>
            </Field>
            <Field label="Date &amp; time" required>
              <input type="datetime-local" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} data-testid="new-appt-start" />
            </Field>
            <Field label="Practitioner" className="col-span-2">
              <select className={inputCls} value={practitionerId} onChange={(e) => setPractitionerId(e.target.value)} data-testid="new-appt-practitioner">
                <option value="">Auto (use day rule)</option>
                {practitioners.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
              </select>
            </Field>
            <div className="col-span-2 bg-cream border border-hairline rounded-md p-3" data-testid="new-appt-recurrence">
              <div className="flex items-start gap-2.5">
                <Repeat className="w-4 h-4 text-forest mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="text-xs font-ui text-charcoal font-medium">Repeat weekly</p>
                  <p className="text-[11px] font-ui text-slate mt-0.5">Same day &amp; time each week for up to 4 weeks. Refuses if any week conflicts.</p>
                  <div className="mt-2 inline-flex bg-white border border-hairline rounded-pill p-1">
                    {[1, 2, 3, 4].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setWeeks(n)}
                        className={`px-3 py-1 text-[11px] font-ui rounded-pill transition-colors ${weeks === n ? "bg-forest text-white" : "text-charcoal hover:text-forest"}`}
                        data-testid={`new-appt-weeks-${n}`}
                      >
                        {n === 1 ? "Just once" : `${n} weeks`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Client picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-ui text-slate">Client</span>
              <div className="inline-flex bg-white border border-hairline rounded-pill p-1">
                {["existing","new"].map((m) => (
                  <button key={m} onClick={() => setClientMode(m)} className={`px-3 py-1 text-xs font-ui rounded-pill capitalize ${clientMode === m ? "bg-charcoal text-white" : "text-slate"}`} data-testid={`client-mode-${m}`}>{m}</button>
                ))}
              </div>
            </div>

            {clientMode === "existing" && (
              <div className="border border-hairline rounded-md p-3">
                {selectedClient ? (
                  <div className="flex items-center justify-between" data-testid="selected-client">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-pill bg-cream flex items-center justify-center text-forest font-medium text-sm">
                        {selectedClient.first_name?.[0]}{selectedClient.last_initial?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-ui text-charcoal">{selectedClient.first_name} {selectedClient.last_initial}.</p>
                        <p className="font-mono text-xs text-slate">{selectedClient.email}</p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedClient(null)} className="text-xs font-ui text-sage hover:text-forest">Change</button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                      <input
                        type="search" placeholder="Search by name, email, or phone…" value={clientQuery}
                        onChange={(e) => setClientQuery(e.target.value)}
                        className="pl-9 pr-3 py-2 w-full bg-white border border-hairline rounded-md text-sm font-ui focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30"
                        data-testid="client-search"
                      />
                    </div>
                    <div className="mt-2 space-y-1 max-h-40 overflow-auto">
                      {clientResults.map((c) => (
                        <button key={c.id} onClick={() => setSelectedClient(c)} className="w-full text-left px-3 py-2 rounded-md hover:bg-sand text-sm font-ui flex items-center justify-between" data-testid={`client-result-${c.id}`}>
                          <span className="text-charcoal">{c.first_name} {c.last_initial}.</span>
                          <span className="font-mono text-xs text-slate">{c.email}</span>
                        </button>
                      ))}
                      {clientQuery && clientResults.length === 0 && (
                        <p className="text-xs font-ui text-slate px-3 py-2">No matches — try "New client".</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {clientMode === "new" && (
              <div className="grid grid-cols-2 gap-3 border border-hairline rounded-md p-3" data-testid="new-client-form">
                <Field label="First name" required compact>
                  <input className={inputCls} value={intake.first_name} onChange={(e) => setIntake({...intake, first_name: e.target.value})} data-testid="new-client-first-name" />
                </Field>
                <Field label="Last initial" required compact>
                  <input maxLength={2} className={inputCls} value={intake.last_initial} onChange={(e) => setIntake({...intake, last_initial: e.target.value})} data-testid="new-client-last-initial" />
                </Field>
                <Field label="Email" required compact className="col-span-2">
                  <input type="email" className={inputCls} value={intake.email} onChange={(e) => setIntake({...intake, email: e.target.value})} data-testid="new-client-email" />
                </Field>
                <Field label="Phone" required compact className="col-span-2">
                  <input type="tel" placeholder="+15551234567" className={inputCls} value={intake.phone} onChange={(e) => setIntake({...intake, phone: e.target.value})} data-testid="new-client-phone" />
                </Field>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-hairline flex items-center justify-between gap-3">
          <p className="text-xs font-ui text-slate">Staff-created — waiver can be collected at the desk.</p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-ui text-slate hover:text-forest" data-testid="new-appt-cancel">Cancel</button>
            <button onClick={submit} disabled={!canSubmit || submitting} className="px-5 py-2.5 bg-forest hover:bg-charcoal disabled:opacity-40 text-white rounded-md text-sm font-medium font-ui inline-flex items-center gap-2 transition-colors" data-testid="new-appt-submit">
              <UserPlus className="w-4 h-4" /> {submitting ? "Creating…" : (weeks > 1 ? `Schedule ${weeks} weeks` : "Schedule")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, compact, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className={`block ${compact ? "text-[11px]" : "text-xs"} font-ui text-slate mb-1 flex items-center gap-1.5`}>
        {label}
        {required && <span className="text-sage text-xs" aria-label="required">•</span>}
      </span>
      {children}
    </label>
  );
}
